<template>
  <div class="payment-create main-content">
    <form action="" @submit.prevent="submitPaymentData">
      <div class="page-header">
        <h3 class="page-title">{{ $t('payments.new_payment') }}</h3>
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/supplier/dashboard" >{{ $t('general.home') }}</router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/supplier/payments" >{{ $tc('payments.payment', 2) }}</router-link>
          </li>
          <li class="breadcrumb-item">{{ $t('payments.new_payment') }}</li>
        </ol>
        <div class="page-actions header-button-container">
          <base-button
            :loading="isLoading"
            :disabled="isLoading"
            icon="save"
            color="theme"
            type="submit">
            {{ $t('payments.register_payment') }}
          </base-button>
        </div>
      </div>
      <div class="payment-card card">
        <div class="card-body">
          <div class="row">
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.date') }}</label><span class="text-danger"> *</span>
                <base-date-picker
                  v-model="formData.payment_date"
                  :invalid="$v.formData.payment_date.$error"
                  :calendar-button="true"
                  calendar-button-icon="calendar"
                  @change="$v.formData.payment_date.$touch()"
                />
                <div v-if="$v.formData.payment_date.$error">
                  <span v-if="!$v.formData.payment_date.required" class="text-danger">{{ $t('validation.required') }}</span>
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $tc('payments.payment_type',1) }}</label>
                <base-select
                  v-model="payment_type"
                  :options="paymentTypes"
                  :searchable="true"
                  :show-labels="false"
                  :placeholder="$t('payments.select_payment_type')"
                  :invalid="$v.payment_type.$error"
                  label="name"

                />
                <div v-if="$v.payment_type.$error">
                  <span v-if="!$v.payment_type.required" class="text-danger">{{ $t('validation.required') }}</span>
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <label class="form-label">{{ $t('payments.customer') }}</label><span class="text-danger"> *</span>
              <base-select
                ref="baseSelect"
                v-model="customer"
                :options="customerList"
                :searchable="true"
                :show-labels="false"
                :allow-empty="false"
                :placeholder="$t('customers.select_a_customer')"
                :invalid="$v.customer.$error"
                label="name"
                track-by="id"
              />
              <div v-if="$v.customer.$error">
                <span v-if="!$v.customer.required" class="text-danger">{{ $tc('validation.required') }}</span>
              </div>
            </div>
            <div class="col-sm-6" >
              <div class="form-group">
                <label class="form-label">{{ $t('payments.invoice') }}</label><span class="text-danger"> *</span>
                <base-select
                  v-model="invoice"
                  :options="invoiceList"
                  :invalid="$v.invoice.$error"
                  :searchable="true"
                  :show-labels="false"
                  :allow-empty="false"
                  :placeholder="$t('invoices.select_invoice')"
                  :custom-label="invoiceWithAmount"
                  track-by="id"
                />
                <div v-if="$v.invoice.$error">
                  <span v-if="!$v.invoice.required" class="text-danger">{{ $tc('validation.required') }}</span>
                </div>
              </div>
            </div>
            <!-- <div class="col-sm-6">
                            <div class="form-group">
                                <label class="form-label">{{ $t('payments.payment_number') }}</label><span class="text-danger"> *</span>
                                <base-prefix-input
                                :invalid="$v.paymentNumAttribute.$error"
                                v-model.trim="paymentNumAttribute"
                                :prefix="paymentPrefix"
                                :readonly="true"
                                @input="$v.paymentNumAttribute.$touch()"
                                />
                                <div v-if="$v.paymentNumAttribute.$error">
                                <span v-if="!$v.paymentNumAttribute.required" class="text-danger">{{ $tc('validation.required') }}</span>
                                <span v-if="!$v.paymentNumAttribute.numeric" class="text-danger">{{ $tc('validation.numbers_only') }}</span>
                                </div>
                            </div>
                        </div> -->
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.payment_mode') }}</label>
                <base-select
                  v-model="formData.payment_method"
                  :options="paymentModes"
                  :searchable="true"
                  :show-labels="false"
                  :placeholder="$t('payments.select_payment_mode')"
                  label="name"
                >
                  <div slot="afterList">
                    <button type="button" class="list-add-button" @click="addPaymentMode">
                      <font-awesome-icon class="icon" icon="cart-plus" />
                      <label>{{ $t('settings.customization.payments.add_payment_mode') }}</label>
                    </button>
                  </div>
                </base-select>
              </div>
            </div>
            <div v-show="formData.payment_type=='Payment'" class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.amount') }}</label><span class="text-danger"> *</span>
                <div class="base-input">
                  <money
                    :class="{'invalid' : $v.formData.amount.$error}"
                    v-model="amount"
                    v-bind="paymentCurrency"
                    :readonly="amountReady"
                    class="input-field"
                  />
                </div>
                <div v-if="$v.formData.amount.$error">
                  <span v-if="!$v.formData.amount.required" class="text-danger">{{ $t('validation.required') }}</span>
                  <span v-if="!$v.formData.amount.between && $v.formData.amount.numeric && amount <= 0" class="text-danger">{{ $t('validation.payment_greater_than_zero') }}</span>
                  <span v-if="!$v.formData.amount.between && amount > 0" class="text-danger">{{ $t('validation.payment_greater_than_due_amount') }}</span>
                </div>
              </div>
            </div>
            <div v-if="formData.payment_type=='Settlement'" class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('general.loanable_amount') }}</label><span class="text-danger"> *</span>
                <div class="base-input">
                  <money
                    :disabled="'true'"
                    v-model="LoanableAmount"
                    v-bind="paymentCurrency"
                    class="input-field"
                  />
                </div>
              </div>
            </div>
            <div v-if="formData.payment_type=='Settlement'" class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('general.funder_settlement') }}</label><span class="text-danger"> *</span>
                <div class="base-input">
                  <money
                    :disabled="'true'"
                    v-model="funder_settlement"
                    v-bind="paymentCurrency"
                    class="input-field"
                    readonly
                  />
                </div>

                <!-- <div class="base-read-box base-read-box-field">
                                    <div v-html="$utils.formatMoney(formData.funder_settlement, customerCurrency)" />
                                </div> -->
              </div>
            </div>

            <div class="col-sm-12 ">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.note') }}</label>
                <base-text-area
                  v-model="formData.notes"
                  @input="$v.formData.notes.$touch()"
                />
                <div v-if="$v.formData.notes.$error">
                  <span v-if="!$v.formData.notes.maxLength" class="text-danger">{{ $t('validation.notes_maxlength') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="formData.payment_type=='Settlement'" class="payment-card card">
        <div class="card-body">
          <div class="row">
            <!-- <div class="col-sm-6">
                            <div class="form-group">
                                <label class="form-label">{{ $t('payments.payment_number') }}</label>
                                <base-prefix-input
                                :invalid="$v.secondPaymentNumAttribute.$error"
                                v-model.trim="secondPaymentNumAttribute"
                                :prefix="paymentPrefix"
                                :readonly="true"
                                @input="$v.secondPaymentNumAttribute.$touch()"
                                />
                                <div v-if="$v.secondPaymentNumAttribute.$error">
                                <span v-if="!$v.secondPaymentNumAttribute.required" class="text-danger">{{ $tc('validation.required') }}</span>
                                <span v-if="!$v.secondPaymentNumAttribute.numeric" class="text-danger">{{ $tc('validation.numbers_only') }}</span>
                                </div>
                            </div>
                        </div> -->

            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.payment_mode') }}</label>
                <base-select
                  v-model="formData.second_payment_method"
                  :options="paymentModes"
                  :searchable="true"
                  :show-labels="false"
                  :placeholder="$t('payments.select_payment_mode')"
                  label="name"
                >
                  <div slot="afterList">
                    <button type="button" class="list-add-button" @click="addPaymentMode">
                      <font-awesome-icon class="icon" icon="cart-plus" />
                      <label>{{ $t('settings.customization.payments.add_payment_mode') }}</label>
                    </button>
                  </div>
                </base-select>
              </div>
            </div>

            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t('general.transaction_fees') }}</label><span class="text-danger"> *</span>
                <div class="base-input">
                  <money
                    :disabled="'true'"
                    v-model="super_admin_settlement"
                    v-bind="paymentCurrency"
                    class="input-field"
                    readonly
                  />
                </div>
                <!-- <div v-if="$v.super_admin_settlement.$error">
                                <span v-if="!$v.super_admin_settlement.required" class="text-danger">{{ $t('validation.required') }}</span>
                                <span v-if="!$v.super_admin_settlement.between && $v.super_admin_settlement.numeric && amount <= 0" class="text-danger">{{ $t('validation.payment_greater_than_zero') }}</span>
                                <span v-if="!$v.super_admin_settlement.between && amount > 0" class="text-danger">{{ $t('validation.payment_greater_than_due_amount') }}</span>
                                </div>   -->
                <!-- <div class="base-read-box base-read-box-field">
                                    <div v-html="$utils.formatMoney(formData.super_admin_settlement, customerCurrency)" />
                                </div> -->
              </div>
            </div>
            <div class="col-sm-12 ">
              <div class="form-group">
                <label class="form-label">{{ $t('payments.note') }}</label>
                <base-text-area
                  v-model="superadmin_settlement_notes"
                  @input="$v.superadmin_settlement_notes.$touch()"
                />
                <div v-if="$v.superadmin_settlement_notes.$error">
                  <span v-if="!$v.superadmin_settlement_notes.maxLength" class="text-danger">{{ $t('validation.notes_maxlength') }}</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

    </form>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import MultiSelect from 'vue-multiselect'
import { validationMixin } from 'vuelidate'
import moment from 'moment'
const { required, between, maxLength, numeric } = require('vuelidate/lib/validators')
export default {
  mixins: [validationMixin],
  data () {
    return {
      isLoading: false,
      amountReady: false,
      formData: {
        customer_id: null,

        payment_number: null,
        second_payment_number: null,

        payment_date: null,
        amount: 0,
        payment_method: null,
        second_payment_method: null,

        invoice_id: null,
        notes: null,

        payment_type: null,

        super_admin_settlement: 0,
        funder_settlement: 0,
        LoanableAmount: 0
      },
      payment_type: null,
      paymentNumAttribute: 0,
      secondPaymentNumAttribute: 0,
      thirdPaymentNumAttribute: 0,
      paymentPrefix: '',
      paymentTypes: [
        {'name': 'Payment'},
        {'name': 'Settlement'}
      ],
      customerList: [],
      customer: null,
      invoiceList: [],
      invoice: null,
      maxPayableAmount: Number.MAX_SAFE_INTEGER,
      customerCurrency: null,

      maxSuperadminAmount: Number.MAX_SAFE_INTEGER,
      maxFunderAmount: Number.MAX_SAFE_INTEGER,

      superadmin_settlement_notes: null,
      funder_settlement_notes: null,
      margin_rate: 0,
      transaction_fees: 0,

      cashflowValue: 0,
      tenor: 0,
      loanOfferedDate: null
    }
  },
  validations () {
    return {
      customer: {
        required
      },
      payment_type: {
        required
      },
      superadmin_settlement_notes: {
        maxLength: maxLength(255)
      },
      funder_settlement_notes: {
        maxLength: maxLength(255)
      },

      invoice: {
        required
      },
      formData: {
        payment_date: {
          required
        },
        amount: {
          required,
          between: between(1, this.maxPayableAmount + 1)
        },
        notes: {
          maxLength: maxLength(255)
        }
      }
      // paymentNumAttribute: {
      //     required,
      //     numeric
      // },
      // secondPaymentNumAttribute:{
      //     required,
      //     numeric
      // },

    }
  },
  created () {
    if (this.isInvoice) {
      this.loadPaymentData()
    }
  },
  computed: {
    ...mapGetters('currency', [
      'defaultCurrencyForInput'
    ]),
    ...mapGetters('payment', [
      'paymentModes'
    ]),
    isInvoice () {
      if (this.$route.name === 'payments.payment.create' || this.$route.name === 'payments.settlement.create.admin') {
        return true
      }
      return false
    },
    amount: {
      get: function () {
        return this.formData.amount / 100
      },
      set: function (newValue) {
        this.formData.amount = newValue * 100
      }
    },
    paymentCurrency () {
      if (this.customer && this.customerCurrency) {
        return {
          decimal: this.customerCurrency.decimal_separator,
          thousands: this.customerCurrency.thousand_separator,
          prefix: this.customerCurrency.symbol + ' ',
          precision: this.customerCurrency.precision,
          masked: false
        }
      } else {
        return this.defaultCurrencyForInput
      }
    },
    LoanableAmount: {
      get: function () {
        return this.margin_rate * (this.cashflowValue) / 100
      },
      set: function (newValue) {
        this.formData.loanableAmount = newValue * 100
      }
    },
    funder_settlement: {
      get: function () {
        if (this.LoanableAmount) {
          return this.LoanableAmount - this.super_admin_settlement
        }
      },
      set: function (newValue) {
        if (this.LoanableAmount) {
          this.formData.funder_settlement = newValue * 100
        }
      }
    },
    super_admin_settlement: {
      get: function () {
        if (this.LoanableAmount) {
          return (this.transaction_fees / 100 * (this.LoanableAmount)) * (this.tenor / 365)
        }
      },
      set: function (newValue) {
        if (this.LoanableAmount) {
          this.formData.super_admin_settlement = newValue * 100
        }
      }
    }
  },
  watch: {
    payment_type (newValue) {
      this.formData.amount = 0
      this.invoiceList = []
      this.customerList = []
      this.customer = null
      this.invoice = null
      if (newValue.name == 'Payment') {
        this.fetchSimpleCustomers()
        this.formData.payment_type = newValue.name
      } else if (newValue.name == 'Settlement') {
        this.fetchParentCustomers()
        this.formData.payment_type = newValue.name
      }
    },
    customer (newValue) {
      if (this.isInvoice == false) {
        this.formData.amount = 0
        this.invoiceList = []
        this.invoice = null
        if (newValue != null) {
          this.customerCurrency = newValue.currency
          this.formData.customer_id = newValue.id
          if (this.formData.payment_type == 'Payment') {
            this.fetchCustomerUnpaidInvoices(newValue.id)
          } else if (this.formData.payment_type == 'Settlement') {
            this.fetchCustomerSettlementInvoices(newValue.id)
          }
        }
      }
    },
    invoice (newValue) {
      if (newValue) {
        this.formData.invoice_id = newValue.id
        this.setPaymentAmountByInvoiceData(newValue.id)
      }
    }
  },
  async mounted () {
    this.$nextTick(() => {
      this.loadData()
    })
  },
  methods: {
    ...mapActions('LoanRequest', [
      'loanRequestStatus'
    ]),
    ...mapActions('payment', [
      'fetchCreatePayment',
      'addPayment',
      'updatePayment',
      'fetchEditPaymentData',
      'funderAddPayment',
      'supplierAddPayment'
    ]),

    ...mapActions('customer', [
      'viewCustomer',
      'fetchCustomers',
      'selectAllCustomers',
      'selectCustomer',
      'deleteCustomer',
      'deleteMultipleCustomers',
      'setSelectAllState'
    ]),
    ...mapActions('invoice', [
      'fetchInvoice',
      'SingleInvoiceArray',
      'fetchInvoiceShow',
      'unpaidCustomerInvoice',
      'needSettleCustomerInvoice'
    ]),
    ...mapActions('modal', [
      'openModal'
    ]),
    async addPaymentMode () {
      this.openModal({
        'title': 'Add Payment Mode',
        'componentName': 'PaymentMode'
      })
    },
    invoiceWithAmount ({ invoice_number, total, due_amount }) {
      if (this.formData.payment_type == 'Settlement') {
        if (this.customerCurrency != null) {
          return `${invoice_number} (${this.$utils.formatGraphMoney(total, this.customerCurrency)})`
        } else {
          return `${invoice_number} (${this.$utils.formatGraphMoney(total, this.defaultCurrencyForInput)})`
        }
      } else {
        if (this.customerCurrency != null) {
          return `${invoice_number} (${this.$utils.formatGraphMoney(due_amount, this.customerCurrency)})`
        } else {
          return `${invoice_number} (${this.$utils.formatGraphMoney(due_amount, this.defaultCurrencyForInput)})`
        }
      }
    },
    async loadData () {
      let response = await this.fetchCreatePayment()
      this.paymentNumAttribute = response.data.nextPaymentNumberAttribute
      this.secondPaymentNumAttribute = response.data.nextSecondPaymentNumberAttribute
      this.thirdPaymentNumAttribute = response.data.nextThirdPaymentNumberAttribute
      this.paymentPrefix = response.data.payment_prefix
      this.formData.payment_date = moment(new Date()).toString()
      return true
    },
    async loadPaymentData () {
      if (this.$route.name === 'payments.payment.create') {
        this.payment_type = {'name': 'Payment'}
        this.formData.payment_type = 'Payment'
      } else if (this.$route.name === 'payments.settlement.create.admin') {
        this.payment_type = {'name': 'Settlement'}
        this.formData.payment_type = 'Settlement'
      }
      let invoiceAmountData = await this.fetchInvoiceShow(this.$route.params.id)
      this.customer = invoiceAmountData.data.invoice.customer
      this.formData.customer_id = invoiceAmountData.data.invoice.customer.id
      this.customerCurrency = invoiceAmountData.data.invoice.customer.currency
      this.invoice = invoiceAmountData.data.invoice
    },
    async fetchSimpleCustomers () {
      let customerRes = await axios.get(`/api/customers/nonRegisterCustomers`)
      this.customerList = customerRes.data.customers
    },
    async fetchParentCustomers () {
      let customerRes = await axios.get(`/api/customers/registerCustomers`)
      this.customerList = customerRes.data.customers
    },
    async fetchCustomerUnpaidInvoices (customerID) {
      let data = {customer_id: customerID }
      try {
        let response = await this.unpaidCustomerInvoice(data)
        if (response.data.result == 'success') {
          this.invoiceList = response.data.invoices
        } else if (response.data.result == 'error') {
          window.toastr['error'](response.data.message)
        }
      } catch (error) {
        this.isLoading = false
        window.toastr['error'](error.response.data.message)
      }
    },
    async fetchCustomerSettlementInvoices (customerID) {
      let data = {customer_id: customerID }
      try {
        let response = await this.needSettleCustomerInvoice(data)
        if (response.data.result == 'success') {
          this.invoiceList = response.data.invoices
        } else if (response.data.result == 'error') {
          window.toastr['error'](response.data.message)
        }
      } catch (error) {
        this.isLoading = false
        window.toastr['error'](error.response.data.message)
      }
    },
    round (value, decimals) {
      return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals)
    },
    // a and b are javascript Date objects
    dateDiffInDays (a, b) {
      const _MS_PER_DAY = 1000 * 60 * 60 * 24
      // Discard the time and time-zone information.
      let utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())
      let utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate())
      return Math.floor((utc2 - utc1) / _MS_PER_DAY)
    },
    async setPaymentAmountByInvoiceData (id) {
      let invoiceAmountData = await this.fetchInvoiceShow(id)
      if (this.formData.payment_type == 'Settlement') {
        this.loanOfferedDate = invoiceAmountData.data.invoice.pre_payment_request_status.find(function (request, index) {
          if (request.status == 'Offered') { return true }
        })
        // test it
        var date1 = new Date(Date())
        var date2 = new Date(invoiceAmountData.data.invoice.pre_payment_request.promised_payment_date)
        this.tenor = this.dateDiffInDays(date1, date2)

        this.margin_rate = invoiceAmountData.data.invoice.pre_payment_request.margin_rate
        this.transaction_fees = invoiceAmountData.data.invoice.pre_payment_request.transaction_fees
        this.formData.amount = invoiceAmountData.data.invoice.total
        this.maxPayableAmount = invoiceAmountData.data.invoice.total
        this.cashflowValue = invoiceAmountData.data.invoice.total
      } else {
        this.formData.amount = invoiceAmountData.data.invoice.due_amount
        this.maxPayableAmount = invoiceAmountData.data.invoice.due_amount
      }
    },
    async submitPaymentData () {
      this.$v.invoice.$touch()
      this.$v.payment_type.$touch()
      this.$v.customer.$touch()
      this.$v.formData.$touch()

      if (this.formData.payment_type == 'Settlement') {
        this.$v.superadmin_settlement_notes.$touch()
        this.$v.funder_settlement_notes.$touch()
      }
      if (this.$v.$invalid) {
        return true
      }
      this.formData.payment_number = this.paymentPrefix + '-' + this.paymentNumAttribute
      this.formData.second_payment_number = this.paymentPrefix + '-' + this.secondPaymentNumAttribute
      this.formData.third_payment_number = this.paymentPrefix + '-' + this.thirdPaymentNumAttribute

      let data = {
        ...this.formData,
        payment_method_id: this.formData.payment_method ? this.formData.payment_method.id : null,
        second_payment_method_id: this.formData.second_payment_method ? this.formData.second_payment_method.id : null,
        // third_payment_method_id: this.formData.second_payment_method ? this.formData.third_payment_method.id : null,
        payment_date: moment(this.formData.payment_date).format('DD/MM/YYYY')
      }
      try {
        this.isLoading = true
        let response = await this.supplierAddPayment(data)
        if (response.data.success) {
          window.toastr['success'](this.$t('payments.created_message'))
          if (response.data.message) {
            window.toastr['success'](response.data.message)
          }
          this.$router.push('/supplier/payments')
          this.isLoading = true
          return true
        }
        if (response.data.error === 'invalid_amount') {
          window.toastr['error'](this.$t('invalid_amount_message'))
          return false
        }
        this.isLoading = false
        window.toastr['error'](response.data.error)
      } catch (err) {
        this.isLoading = false
        if (err.response.data.errors.payment_number) {
          window.toastr['error'](err.response.data.errors.payment_number)
          return true
        }
        window.toastr['error'](err.response.data.message)
      }
    }
  }

}
</script>
